import { z } from 'zod';
/**
 * Perfis de operação do agente EndCopilot.
 * - MONITORING  (P1): instalação voluntária, inventário, heartbeat.
 * - ASSISTED    (P2): P1 + launcher opcional e filtro VPN com consentimento.
 *                     NÃO impede desinstalação, modo seguro ou acesso às configurações.
 * - CORPORATE   (P3): Device Owner provisionado oficialmente (Android Enterprise).
 */
export const ProfileSchema = z.enum(['MONITORING', 'ASSISTED', 'CORPORATE']);
/**
 * Capacidades relatadas pelo agente, detectadas em tempo de execução no aparelho.
 * O servidor nunca "promove" um aparelho: o perfil efetivo deriva só deste relatório.
 */
export const CapabilityReportSchema = z.object({
    /** DevicePolicyManager.isDeviceOwnerApp(packageName) */
    isDeviceOwner: z.boolean(),
    /** O launcher EndCopilot é o HOME padrão (escolhido pelo usuário ou fixado pelo DO). */
    isDefaultLauncher: z.boolean(),
    /** VpnService.prepare() retornou null (consentimento concedido) e o túnel está ativo. */
    vpnFilterActive: z.boolean(),
    /** Lock task permitido para o pacote (só possível como DO). */
    lockTaskPermitted: z.boolean(),
    androidSdkInt: z.number().int().min(28),
});
/** Controles que o portal pode exibir como "realmente aplicados". */
export const ControlSchema = z.enum([
    'INVENTORY',
    'HEARTBEAT',
    'LAUNCHER_HOME',
    'VPN_DOMAIN_FILTER',
    'UNINSTALL_BLOCK',
    'LOCK_TASK_KIOSK',
    'USER_RESTRICTIONS',
    'SILENT_APP_INSTALL',
]);
/** Controles que exigem Device Owner. Nunca devem aparecer fora do P3. */
export const DEVICE_OWNER_ONLY_CONTROLS = [
    'UNINSTALL_BLOCK',
    'LOCK_TASK_KIOSK',
    'USER_RESTRICTIONS',
    'SILENT_APP_INSTALL',
];
export class InconsistentCapabilityReportError extends Error {
    constructor(message) {
        super(message);
        this.name = 'InconsistentCapabilityReportError';
    }
}
/**
 * Calcula o perfil efetivo a partir das capacidades relatadas.
 * Lança erro se o relatório for incoerente (ex.: lock task sem Device Owner),
 * para que um agente convencional nunca seja tratado como corporativo.
 */
export function computeEffectiveProfile(report) {
    if (report.lockTaskPermitted && !report.isDeviceOwner) {
        throw new InconsistentCapabilityReportError('lockTaskPermitted exige isDeviceOwner');
    }
    if (report.isDeviceOwner)
        return 'CORPORATE';
    if (report.isDefaultLauncher || report.vpnFilterActive)
        return 'ASSISTED';
    return 'MONITORING';
}
/** Lista os controles efetivamente aplicáveis dadas as capacidades. */
export function appliedControls(report) {
    const profile = computeEffectiveProfile(report);
    const controls = ['INVENTORY', 'HEARTBEAT'];
    if (report.isDefaultLauncher)
        controls.push('LAUNCHER_HOME');
    if (report.vpnFilterActive)
        controls.push('VPN_DOMAIN_FILTER');
    if (profile === 'CORPORATE') {
        controls.push('UNINSTALL_BLOCK', 'USER_RESTRICTIONS', 'SILENT_APP_INSTALL');
        if (report.lockTaskPermitted)
            controls.push('LOCK_TASK_KIOSK');
    }
    return controls;
}
//# sourceMappingURL=profiles.js.map